"use client";

import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";

// Shared mac-window frame for real product screenshots (framework + playbook,
// extracted 2026-08-01). Click-to-expand lightbox: the image is a button that
// opens a full-viewport overlay (Esc, backdrop click, or the close button
// dismiss it; body scroll locks while open). `chip` renders an optional
// honesty label in the title bar (e.g. "Sample data", "Demo site"); omit it
// for no label.

const TEXT = "#EDEDED";
const TEXT_DIM = "#a0a0a0";
const TEAL = "#4FCDC4";
const CARD_BORDER = "rgba(255,255,255,0.04)";

export default function ScreenshotFrame({ src, alt, title, caption, chip = null }) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  // A caller that forgets `title` used to ship "Expand screenshot: undefined"
  // to screen readers and an empty title bar (caught 2026-08-07 across 14 blog
  // call sites). Fall back to the first clause of the alt text, which is always
  // present and always describes the shot.
  const derived = (alt || "").split(/[,:.]/)[0].trim();
  const label = title || (derived.length > 60 ? derived.slice(0, 57).trimEnd() + "..." : derived) || "screenshot";

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, close]);

  return (
    <figure style={{ margin: "28px 0" }}>
      <div style={{
        borderRadius: 12, border: `1px solid ${CARD_BORDER}`,
        background: "rgba(255,255,255,0.02)", overflow: "hidden",
      }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "8px 12px", borderBottom: `1px solid ${CARD_BORDER}`,
          background: "rgba(255,255,255,0.03)",
        }}>
          <span aria-hidden="true" style={{ display: "flex", gap: 5 }}>
            {["#ff5f57", "#febc2e", "#28c840"].map((c) => (
              <span key={c} style={{ width: 9, height: 9, borderRadius: "50%", background: c, opacity: 0.75, display: "inline-block" }} />
            ))}
          </span>
          <span style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "var(--mono)", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
          {chip && (
            <span style={{
              fontSize: 10, color: TEAL, fontFamily: "var(--mono)", textTransform: "uppercase",
              letterSpacing: "0.06em", padding: "2px 8px", borderRadius: 6,
              border: "1px solid rgba(79,205,196,0.3)", background: "rgba(79,205,196,0.08)", flexShrink: 0,
            }}>
              {chip}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={`Expand screenshot: ${label}`}
          style={{
            display: "block", width: "100%", padding: 0, margin: 0,
            border: "none", background: "transparent", cursor: "zoom-in",
          }}
        >
          <img src={src} alt={alt} loading="lazy" style={{ display: "block", width: "100%", height: "auto" }} />
        </button>
      </div>
      <figcaption style={{ fontSize: 12, color: TEXT_DIM, fontFamily: "var(--body)", lineHeight: 1.5, marginTop: 8 }}>
        {caption}
      </figcaption>

      {/* Portaled to <body>: the FadeIn wrappers around every section keep a
          CSS transform, which turns them into containing blocks for
          position: fixed — without the portal the overlay centers inside the
          wrapper instead of the viewport (the "blank/cut off" lightbox bug,
          2026-08-01). Same escape pattern as the dnd-kit DragOverlay portal. */}
      {open && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label={label}
          onClick={close}
          style={{
            position: "fixed", inset: 0, zIndex: 1000,
            background: "rgba(9,9,11,0.94)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "32px 16px", cursor: "zoom-out",
          }}
        >
          <img
            src={src}
            alt={alt}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "min(1680px, 96vw)", maxHeight: "88vh",
              width: "auto", height: "auto", cursor: "default",
              borderRadius: 10, border: "1px solid rgba(255,255,255,0.1)",
              boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
            }}
          />
          <button
            type="button"
            onClick={close}
            aria-label="Close expanded screenshot"
            style={{
              position: "absolute", top: 16, right: 16,
              width: 44, height: 44, borderRadius: 10,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)",
              color: TEXT, fontSize: 22, lineHeight: 1, cursor: "pointer",
            }}
          >
            &#215;
          </button>
        </div>,
        document.body
      )}
    </figure>
  );
}
